import { createDraftSafeSelector, createSlice } from '@reduxjs/toolkit';
import { regionApi, regionHaInstanceApi } from '../services';
import { RootState } from './index';
import { regionHaApi } from '../services/regionHa';

interface HaInstanceStore {
  accountRegionInstances: Record<string, Record<string, any>>;
  accountRegionHaIns: Record<string, Record<string, any>>;
  accountHa: Record<string, Record<string, any>>;
  regions: Record<string, any>;
}

export const HealthStatusTextMapping: Record<string, string> = {
  Green: 'Available',
  Yellow: 'Requires Attention',
  Red: 'Error'
};
export const HaStatusTextMapping: Record<string, string> = {
  INIT: 'Configuring',
  IDLE: 'Active'
};
const initialState: HaInstanceStore = {
  accountRegionInstances: {},
  accountRegionHaIns: {},
  accountHa: {},
  regions: {}
};
const haInstanceSlice = createSlice({
  name: 'haInstance',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      regionApi.endpoints.regionLogin.matchFulfilled,
      (state, { payload }) => {
        const region: Record<string, any> = payload || {};
        if (region?.region) {
          state.regions[region?.region] = {
            ...region
          };
        }
      }
    );
    builder.addMatcher(
      regionHaApi.endpoints.getRegionsHaList.matchFulfilled,
      (state, { payload, meta }) => {
        const account_id = meta.arg.originalArgs.account_id;
        if (payload?.length) {
          const haArr = payload
            .map((obj: Record<string, any>) => {
              const region = obj?.region;
              const data = obj?.data;
              return data?.map((haObj: Record<string, any>) => ({
                ...haObj,
                region: region
              }));
            })
            .flat(1);
          const haObjs = haArr?.reduce(
            (res: Record<string, any>, haObj: Record<string, any>) => {
              return { ...res, [haObj?.id]: haObj };
            },
            {}
          );
          state.accountHa = { ...state.accountHa, [account_id]: haObjs };
        } else {
          state.accountHa = { ...state.accountHa, [account_id]: {} };
        }
      }
    );

    builder.addMatcher(
      regionHaInstanceApi.endpoints.getRegionsInstanceList.matchFulfilled,
      (state, { payload, meta }) => {
        const account_id = meta.arg.originalArgs.account_id;
        const data: Record<string, any>[] = payload || [];
        let newRegions: Record<string, any> = {};
        let newHaObjects: Record<string, any> = {};
        data.map((obj: Record<string, any>, i: number) => {
          const instances = obj?.data || [];
          const regionName = obj?.region?.region || obj?.region?.alias || '';
          const instObjs = instances?.map((i: Record<string, any>) => ({
            ...i,
            region: obj.region,
            cluster_info: getFormatClusterInfo(i.cluster_info)
          }));
          newRegions[regionName] = instObjs;
          const haInstObjs = instObjs?.filter(
            (i: Record<string, any>) => i.ha_id
          );
          const haIds = new Set(
            haInstObjs?.map(
              (i: Record<string, any>) => `${i.ha_id}|${i.ha_name}`
            )
          );
          const idArray = Array.from(haIds) as Array<string>;
          newHaObjects[regionName] = idArray.reduce((acc, id: string) => {
            const arr = id.split('|');
            if (arr.length > 1) {
              const id = arr[0];
              const name = arr[1];
              // const instances = haInstObjs.filter(
              //   (obj: Record<string, any>) => obj.ha_id === id
              // );
              const instances = haInstObjs.filter(
                (obj: Record<string, any>) => obj.ha_id === id
              );
              const cluster_health: string = instances.every(
                (instance: Record<string, any>) =>
                  instance.cluster_info?.health_status === 'Green'
              )
                ? HealthStatusTextMapping.Green
                : instances.some(
                      (instance: Record<string, any>) =>
                        instance.cluster_info?.health_status === 'Red'
                    )
                  ? HealthStatusTextMapping.Red
                  : instances.some(
                        (instance: Record<string, any>) =>
                          instance.cluster_info?.health_status === 'Yellow'
                      )
                    ? HealthStatusTextMapping.Yellow
                    : 'Unknown';
              return {
                ...acc,
                [id]: {
                  ha_id: id,
                  ha_name: name,
                  ha_health: cluster_health,
                  regionObj: obj?.region,
                  instances: instances
                }
              };
            }
            return acc;
          }, {});
        });

        state.accountRegionInstances = {
          ...state.accountRegionInstances,
          [account_id]: newRegions
        };
        state.accountRegionHaIns = {
          ...state.accountRegionHaIns,
          [account_id]: newHaObjects
        };
      }
    );

    builder.addMatcher(
      regionHaInstanceApi.endpoints.getHaInstanceState.matchFulfilled,
      (state, { payload, meta }) => {
        const status = payload.status;
        const region_alias = meta.arg.originalArgs.region?.alias;
        const account_id = meta.arg.originalArgs.account_id;
        const instance_id = meta.arg.originalArgs.instance_id;
        const action = meta.arg.originalArgs.action;
        const actionText: Record<string | number, string> = {
          3: 'Reboot',
          4: 'Creating Snapshot',
          5: 'Reverting Snapshot'
        };
        if (status && region_alias && instance_id && account_id) {
          const oldData =
            state.accountRegionInstances[account_id][region_alias];
          if (oldData) {
            const index = oldData.findIndex(
              (o: Record<string, any>) => o.instance_id === instance_id
            );
            let newData = [...oldData];
            newData[index] = {
              ...oldData[index],
              instance_get_state: status,
              action: ['ACTIVE', 'SUCCESS', 'ERROR'].includes(status)
                ? 0
                : action,
              actionText: ['ACTIVE', 'SUCCESS', 'ERROR'].includes(status)
                ? ''
                : actionText[action]
            };
            state.accountRegionInstances[account_id][region_alias] = [
              ...newData
            ];
          }
        }
      }
    );
  }
});

const { reducer, actions } = haInstanceSlice;
const getFormatClusterInfo = (clusterObj: Record<string, string>) => {
  const cluster_info =
    clusterObj ||
    {
      // node_role: 'Primary', //Primary/Secondary
      // health_status: 'Green', //Green/Yellow/Red
      // node_status: 'Active', // Active/Passive
      // timestamp: 1708941966
    };
  return {
    ...cluster_info,
    node_role: cluster_info?.node_role || 'Unknown',
    health_status_text:
      HealthStatusTextMapping[cluster_info?.health_status] ||
      cluster_info?.health_status ||
      'Unknown'
  };
};

export const selectAccountRegionInstances = (state: RootState) =>
  state.haInstance.accountRegionInstances;
export const selectAccountRegionHaIns = (state: RootState) =>
  state.haInstance.accountRegionHaIns;
export const selectAccountHaList = (state: RootState) =>
  state.haInstance.accountHa;

// export const selectAllInstances = createDraftSafeSelector(
//   [selectRegionInstances],
//   (ids) => Object.values(ids).flat(1)
// );
// export const selectAllHa = createDraftSafeSelector(
//   [selectRegionHaIns],
//   (haIds) =>
//     Object.keys(haIds)
//       .map((key) =>
//         Object.values(haIds[key]).map((obj: Record<string, any>) => ({
//           ...obj,
//           region: key
//         }))
//       )
//       .map((obj: Record<string, any>) => Object.values(obj))
//       .flat(1)
// );

// export const hasHa = createDraftSafeSelector(
//   [selectRegionHaIns],
//   (haIds) => Object.keys(haIds).length > 0
// );
// export const selectHaCreatable = createDraftSafeSelector(
//   [selectRegionInstances],
//   (ids) => {
//     const regions = ids;
//     const res = Object.keys(regions)?.reduce(
//       (acc: Record<string, any>, key: string) => {
//         const instances: Record<string, any>[] = regions[key];
//         const availableInstances = instances.filter((obj) => !obj.ha_id);
//         if (availableInstances.length > 1) {
//           acc[key] = availableInstances;
//         }
//         return acc;
//       },
//       {}
//     );
//     return res;
//   }
// );

export const selectAccountAllInstances = createDraftSafeSelector(
  [selectAccountRegionInstances],
  (accounts): Record<string, Record<string, any>> => {
    return Object.keys(accounts).reduce((rec, accountId) => {
      const ids: Record<string, Record<string, any>[]> = accounts[accountId];
      return { ...rec, [accountId]: Object.values(ids).flat(1) };
    }, {});
  }
);
export const selectAccountAllHa = createDraftSafeSelector(
  [selectAccountRegionHaIns, selectAccountHaList],
  (accounts, haList): Record<string, Record<string, any>[]> => {
    return Object.keys(accounts).reduce((rec, accountId) => {
      const accountHaList = haList[accountId] || {};
      let noMemberHaList: Record<string, any>[] = [];
      if (accountHaList) {
        noMemberHaList =
          Object.values(accountHaList)
            ?.filter(
              (obj: Record<string, any>) =>
                !obj?.instance_members || !obj?.instance_members?.length
            )
            ?.map((obj: Record<string, any>) => ({
              ha_id: obj?.id,
              ha_name: obj?.name,
              ha_status: obj?.status,
              ha_status_text: HaStatusTextMapping[obj?.status] || obj?.status,
              ha_desc: obj?.desc,
              region_alias: obj?.region?.alias
            })) || [];
      }
      const haIds: Record<string, Record<string, any>> = accounts[accountId];
      const arr = Object.keys(haIds)
        .map((key) =>
          Object.values(haIds[key]).map((obj: Record<string, any>) => ({
            ...obj,
            ha_status: accountHaList[obj?.ha_id]?.status,
            ha_status_text:
              HaStatusTextMapping[accountHaList[obj?.ha_id]?.status] ||
              accountHaList[obj?.ha_id]?.status,
            ha_desc: accountHaList[obj?.ha_id]?.desc,
            region_alias: key
          }))
        )
        .map((obj: Record<string, any>) => Object.values(obj))
        .flat(1);
      return { ...rec, [accountId]: [...arr, ...noMemberHaList] };
    }, {});
  }
);
// export const accountHasHa = createDraftSafeSelector(
//   [selectAccountRegionHaIns],
//   (accounts): Record<string, Record<string, any>> => {
//     return Object.keys(accounts).reduce((rec, accountId) => {
//       const haIds = accounts[accountId];
//       return { ...rec, [accountId]: Object.keys(haIds).length > 0 };
//     }, {});
//   }
// );
export const selectAccountHaCreatable = createDraftSafeSelector(
  [selectAccountRegionInstances],
  (accounts): Record<string, Record<string, any>> => {
    return Object.keys(accounts).reduce((rec, accountId) => {
      const ids: Record<string, Record<string, any>[]> = accounts[accountId];
      const res = Object.keys(ids)?.reduce(
        (acc: Record<string, any>, key: string) => {
          const instances: Record<string, any>[] = ids[key];
          // add support_ha for TIP
          const availableInstances = instances?.filter(
            (obj) => !obj.ha_id && obj.support_ha !== false
          );
          // need show all the instances to create ha instance options
          if (availableInstances?.length > 0) {
            acc[key] = availableInstances;
          }
          return acc;
        },
        {}
      );
      return { ...rec, [accountId]: res };
    }, {});
  }
);
export default reducer;
